import { Component, Inject, Input, QueryList, ViewChild, ViewChildren, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { formatDistanceToNow } from 'date-fns';
import { ChatMessage, ChatService } from 'src/app/_services/chat.service';
import { PersistedProfile, ProfileService } from 'src/app/_services/profiles.service';
import { DATE_FNS_LOCALE } from 'src/app/app.module';
import { AuthenticationService } from 'src/app/_services';

@Component({
  selector: 'chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, OnDestroy {
  
  messages: ChatMessage[] = [];
  loading = true;
  focusChat: boolean = false;
  
  @Input()
  chatId: number | undefined;
  
  @Input()
  itemToSellId: number;
  
  @Input()
  emailForReplies: string | undefined;
  
  @Input()
  pollForNewMessages: boolean = true;
  
  private polling: boolean = false;
  private profile: PersistedProfile | null;
  private pollingTimer: any;
  
  messageForm = new FormGroup({
    message: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(1000)]),
  });
  
  @ViewChild('messageFormComponent') messageFormComponent!: NgForm;
  
  @ViewChildren("newMessageTextInput")
  public newMessageTextInput: QueryList<MatInput>
  
  constructor(
    private profileService: ProfileService,
    route: ActivatedRoute,
    private chatService: ChatService,
    private authenticationService: AuthenticationService,
    @Inject(DATE_FNS_LOCALE) private dateFnsLocale: Locale
    ) {
      
    route.queryParams.subscribe(queryParams => {
      console.log("checking if chat needs to be focused");
      if (queryParams["focusChat"]) {
        
        console.log("Focusing chat");
        this.focusChat = !!queryParams["focusChat"];
        
        if (this.focusChat) {
          setTimeout(() => {
            this.focusInput();
          });
        }
      }
    })
  }
  
  ngOnDestroy(): void {
    if (this.pollingTimer) {
      clearTimeout(this.pollingTimer);
    }
  }
  
  async ngOnInit() {
    
    if (this.authenticationService.isAuthenticated()) {
      this.profile = (await this.profileService.list())[0];//TODO
    } else {
      this.profile = null;
    }

    await this.loadMessages();
    
    if (this.chatId) {
      this.schedulePoll();
    }
    
    this.newMessageTextInput.changes.subscribe((inputs: QueryList <MatInput>) => {
     if (this.focusChat && inputs.first) {
        setTimeout(() => {
          this.focusInput();
        });
     } else {
       console.log("No need to focus chat");
     }
    });
  }
  
  private focusInput() {
    
    if (!this.newMessageTextInput.first) {
      console.log("No input to focus");
      return;
    }
    
    console.log("Focussing chat message input");
    this.newMessageTextInput.first.focus();
  }
  
  private async loadMessages() {
    
    if (!this.chatId) {
      console.log("Skipping load of messages, chat not started");
      this.loading = false;
      return;
    }
    
    const loadedMessages = (await this.chatService.getChatMessages(this.chatId!))
      .sort((a, b) => a.createdTime.valueOf() - b.createdTime.valueOf());
      
    if (loadedMessages.length !== this.messages!.length) {
      this.messages = loadedMessages;
    }
    
    this.loading = false;
      
    this.scheduleMarkMessageAsRead();
  }
  
  private async markMessageAsRead(messageId: number) {
    
    if (document.hidden) {
      return;
    }
    
    await this.chatService.markAsRead(this.chatId!, messageId);
    this.messages.find(message => message.id === messageId)!.unread = false;
  }
  
  private async scheduleMarkMessageAsRead() {
    
    this.messages
      .filter(message => message.unread && !this.isCurrentUserAuthorOfMessage(message))
      .forEach(message => {
        setTimeout(() => { this.markMessageAsRead(message.id) }, 3000);
      });
  }
  
  isCurrentUserAuthorOfMessage(message: ChatMessage): boolean {
    
    if (!this.profile && !message.authorUsername) {
      return true;
    }
    
    return message.authorUsername === this.profile!.username;
  }
  
  getMessageAuthor(message: ChatMessage): string {
    
    if (this.isCurrentUserAuthorOfMessage(message)) {
      return $localize `Me`;
    }
    
    if (message.authorUsername) {
      return message.authorUsername;
    }
    
    return message.fromSeller ? $localize `Owner` : $localize `Buyer`;
  }
  
  private async schedulePoll(): Promise<void> {
    
    if (!this.pollForNewMessages) {
      console.log("Polling for new messages disabled");
      return;
    }
    
    if (this.polling) {
      console.log("Already polling");
      return;
    }
    
    this.pollingTimer = setTimeout(() => { this.poll() }, 10000);
    this.polling = true;
  }
  
  async poll(): Promise<void> {
    
    console.log("Polling messages");
    await this.loadMessages();
    
    console.log("Scheduling another poll");
    this.pollingTimer = setTimeout(() => { this.poll() }, 10000);      
  }
 
  private async startChat() {
    const chat = await this.chatService.startChat(this.itemToSellId, this.emailForReplies);
    this.chatId = chat.id;
  }
 
  getHumanReadableMessageTimestamp(date: Date): string {
    return formatDistanceToNow(date, { addSuffix: true, locale: this.dateFnsLocale });
  }
 
  async sendMessage(): Promise<void> {
    
    if (this.messageForm.invalid) {
      console.log("Invalid");
      return;
    }
    
    this.messageForm.disable();
    
    if (!this.chatId) {
      await this.startChat();
      this.schedulePoll();
    }
    
    const postedMessage = await this.chatService.sendMessage(this.chatId!, this.messageForm.controls["message"].value!);
    
    this.messages.push(postedMessage);
    
    this.messageFormComponent.resetForm()
    this.messageForm.enable();
  }
}
